import { useEffect, useState } from 'react';
import api from '../../api';
import { deliveredFromWire } from '../../containers/Batteries/lib/deliveredWire';
import { chunkDeliveryWindow, DELIVERED_MAX_LIMIT } from '../../containers/Batteries/lib/recordWindow';

const EMPTY = { reports: [], isReady: false, error: false, chunkCount: 0, truncatedChunks: 0 };

/**
 * Every settled delivery report of a battery in `[from, to]`, oldest first by `end`.
 *
 * The listing has an un-pageable 1000-row cap, so the window is fanned out over
 * `chunkDeliveryWindow` sub-windows and merged. `truncatedChunks` counts sub-windows that came
 * back exactly full — chunk sizing is meant to make that impossible, so a non-zero count means
 * the window was denser than the assumed 15-minute settlement and is missing reports.
 *
 * @param {string|null} batteryId
 * @param {{from: string, to: string}|null} range
 */
const useBatteryDelivered = (batteryId, range) => {
    const [state, setState] = useState(EMPTY);
    const from = range?.from ?? null;
    const to = range?.to ?? null;

    useEffect(() => {
        if (!batteryId || !from || !to) {
            setState({ ...EMPTY, isReady: true });
            return undefined;
        }
        let active = true;
        setState(EMPTY);

        const chunks = chunkDeliveryWindow(from, to);
        Promise.all(chunks.map(chunk => api.get_battery_delivered(batteryId, chunk.from, chunk.to, DELIVERED_MAX_LIMIT)))
            .then(results => {
                if (!active) {
                    return;
                }
                const pages = results.map(res => res?.data ?? []);
                const reports = pages
                    .flat()
                    .map(deliveredFromWire)
                    .sort((a, b) => Date.parse(a.end) - Date.parse(b.end));
                setState({
                    reports,
                    isReady: true,
                    error: false,
                    chunkCount: chunks.length,
                    truncatedChunks: pages.filter(page => page.length >= DELIVERED_MAX_LIMIT).length,
                });
            })
            .catch(() => {
                if (active) {
                    setState({ ...EMPTY, isReady: true, error: true, chunkCount: chunks.length });
                }
            });

        return () => {
            active = false;
        };
    }, [batteryId, from, to]);

    return state;
};

export default useBatteryDelivered;
